import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { getCollectionRequest } from "services/apiRequests";
import { clockify, getLastDates, singlify } from "utils/utils";
import ChartTooltip from "./ChartTooltip";
import { usePlatform } from "components/utils/PlatformContext";
import { logoutFunction } from "services/authRequests";

interface ActivityChartParams {
  userClassId: string;
}

const ActivityChart: React.FC<ActivityChartParams> = ({ userClassId }) => {
  // States definition
  const [data, setData] = useState<{ day: string; value: number }[]>([]);
  const [chartData, setChartData] = useState<{ day: string; value: number }[]>(
    []
  );
  const [activityPeriod, setActivityPeriod] = useState("week");
  const [yAxisWidth, setYAxisWidth] = useState(0);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const { studentsTerminology } = usePlatform();

  /**
   * Function to define the max width of YAxis labels
   */
  useEffect(() => {
    // Find the longest Y-axis label width
    const labels = document.querySelectorAll(
      ".recharts-yAxis .recharts-cartesian-axis-tick-value tspan"
    );

    if (labels) {
      let maxWidth = 0;

      labels.forEach((label: any) => {
        const labelWidth = label.getBBox().width;
        if (labelWidth > maxWidth) {
          maxWidth = labelWidth;
        }
      });

      // Add some padding to the calculated width
      setYAxisWidth(maxWidth + 5);
    }
  }, [chartData, activityPeriod]);

  /**
   * Function used to get the data
   */
  useEffect(() => {
    fetchChartData();
  }, []);

  /**
   * Function used to update the display data if the activity period change
   */
  useEffect(() => {
    setChartData(data.slice(activityPeriod === "week" ? -7 : 0));
  }, [data, activityPeriod]);

  /**
   * Function used to get the chart data
   * @returns
   */
  const fetchChartData = async () => {
    // Turn on the loading data flag
    setIsLoadingData(true);

    // Get the data
    const response = await getCollectionRequest(
      `/api/usage/class-activity?classId=${userClassId}`
    );

    // Check if the response is successful
    if (!response.successful) {
      // Check if it's not successful because of a token expiration
      if (response.logout) {
        return await logoutFunction();
      }

      return [];
    }

    // Get the data
    const data = response.content;

    // Transform the query into an object to process the data later
    const dataObj: { [key: string]: number } = data.reduce(
      (
        output: { [key: string]: number },
        entry: {
          _id: string;
          totalTime: number;
        }
      ) => {
        // Assign the value
        output[entry._id] = entry.totalTime;
        return output;
      },
      {}
    );

    // Process data
    const lastMonth = getLastDates(30);
    // Create the chart data based on the current data
    const processedData = lastMonth.map((date: string) => {
      return { day: date, value: (dataObj[date] || 0)};
    });

    // Set the processed
    setData(processedData);

    // Turn off the flag for setting data
    setIsLoadingData(false);
  };

  /**
   * Function used to change the activity period state once pressing
   * the dropdown menu
   * @param event
   */
  const handlePeriodChange = (event: any) => {
    setActivityPeriod(event.target.value);
  };

  return (
    <div className="h-[417px] p-6 bg-neutral-25 rounded-3xl flex flex-col justify-start items-start gap-8">
      <div className="w-full flex justify-between items-center">
        <div className="flex flex-col justify-start items-start">
          <div className="text-neutral-600 text-lg font-normal font-sans leading-snug">
            Activity
          </div>
          <div className="text-neutral-400 text-[10px] font-normal font-sans leading-3 tracking-tight">
            {singlify(studentsTerminology)} Engagement in active{" "}
            <span className="font-bold">{/*yAxisLabel.toLowerCase()*/"time"}</span>
          </div>
        </div>
        <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={activityPeriod}
            onChange={handlePeriodChange}
            label="Period"
          >
            <MenuItem value="week">Week</MenuItem>
            <MenuItem value="month">Month</MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className="w-full h-[200px]">
        {!isLoadingData && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="day" type="category" fontSize={12} />
              <YAxis
                width={yAxisWidth}
                fontSize={12}
                allowDecimals={
                  true
                } /*label={{value: yAxisLabel, angle: -90, position: "insideLeft"}}*/
                tickFormatter={(value) => clockify(value)}
              />
              <Tooltip content={<ChartTooltip />} />
              <Bar
                barSize={25}
                radius={4}
                dataKey="value"
                fill="#16a7b6"
                isAnimationActive={true}
              >
                {chartData.map((_, index: number) => (
                  <rect key={`bar-${index}`} className="bar-animation" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="w-full flex flex-col justify-start items-start gap-2">
        <div className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide">
          Legend
        </div>
        <div className="text-neutral-500 text-[10px] font-normal font-sans leading-3 tracking-tight">
          Numeric units represent time which the{" "}
          {studentsTerminology.toLowerCase()} spent interacting with the
          learning content
        </div>
      </div>
    </div>
  );
};

export default ActivityChart;
